import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input, Select, Space, Alert, Row, Col, Card, Statistic, Tag, Modal, message } from 'antd';
import {
  TeamOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import useAdminDashboard from '../../hooks/useAdminDashboard';
import adminApi from '../../services/tenantService';
import AdminTenantList from '../../components/admin/AdminTenantList';
import AdminTenantDrawer from '../../components/admin/AdminTenantDrawer';
import { Tenant, PlanType, SubscriptionStatus, SubscriptionPlan } from '../../types/tenant';

const { Search } = Input;

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Trial', value: 'trial' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Expired', value: 'expired' },
];

const AdminDashboard: React.FC = () => {
  const {
    tenants, total, stats, selectedTenant, loading, error,
    employees, operations, financials, apiUsage, auditLogs,
    fetchTenants, fetchStats, fetchTenantDetails,
    fetchTenantEmployees, fetchTenantOperations,
    fetchTenantFinancials, fetchTenantApiUsage, fetchTenantAuditLogs,
    suspendTenant, resumeTenant, cancelTenant, changePlan,
    impersonateTenant, clearSelectedTenant,
  } = useAdminDashboard();

  const [searchParams, setSearchParams] = useSearchParams();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const pageSize = 50;

  // Fetch plans once for filter dropdown and drawer
  useEffect(() => {
    adminApi.getPlans().then(setPlans).catch(() => {});
  }, []);

  const searchQuery = searchParams.get('q') || '';
  const selectedPlans = (searchParams.get('plan')?.split(',').filter(Boolean) || []) as PlanType[];
  const selectedStatuses = (searchParams.get('status')?.split(',').filter(Boolean) || []) as SubscriptionStatus[];

  const updateSearchParams = useCallback((updates: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, val]) => {
        if (val) next.set(key, val);
        else next.delete(key);
      });
      return next;
    });
  }, [setSearchParams]);

  const handleSearch = useCallback((value?: string) => {
    const q = value ?? searchQuery;
    updateSearchParams({ q });
  }, [searchQuery, updateSearchParams]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedPlans.join(','), selectedStatuses.join(',')]);

  // Fetch tenants whenever URL params or page change
  useEffect(() => {
    const params: Record<string, string | number> = {
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
    };
    if (searchQuery) params.search = searchQuery;
    if (selectedPlans.length) params.plan = selectedPlans.join(',');
    if (selectedStatuses.length) params.status = selectedStatuses.join(',');
    fetchTenants(params);
  }, [searchQuery, selectedPlans.join(','), selectedStatuses.join(','), currentPage, fetchTenants]);

  const handleViewDetails = useCallback(async (tenant: Tenant) => {
    await fetchTenantDetails(tenant.id);
    setIsDrawerOpen(true);
  }, [fetchTenantDetails]);

  const handleSuspend = useCallback((tenant: Tenant) => {
    let suspendReason = '';
    Modal.confirm({
      title: `Suspend "${tenant.companyName}"?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <Input.TextArea
          placeholder="Enter reason for suspension…"
          rows={3}
          onChange={(e) => { suspendReason = e.target.value; }}
        />
      ),
      okText: 'Suspend',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!suspendReason.trim()) {
          message.warning('A reason is required — it goes in the audit log.');
          throw new Error('Reason required');
        }
        await suspendTenant(tenant.id, suspendReason.trim());
        fetchTenants();
        fetchStats();
      },
    });
  }, [suspendTenant, fetchTenants, fetchStats]);

  const handleResume = useCallback(async (tenant: Tenant) => {
    await resumeTenant(tenant.id);
    fetchTenants();
    fetchStats();
  }, [resumeTenant, fetchTenants, fetchStats]);

  const handleImpersonate = useCallback((tenant: Tenant) => {
    Modal.confirm({
      title: `Login as "${tenant.companyName}"?`,
      icon: <ExclamationCircleOutlined />,
      content: 'You will be redirected to the dispatch console as this tenant. Your current session will be preserved.',
      okText: 'Login as Tenant',
      onOk: async () => {
        try {
          const result = await impersonateTenant(tenant.id);
          localStorage.setItem('impersonation_token', result.token);
          localStorage.setItem('original_token', localStorage.getItem('token') || '');
          localStorage.setItem('token', result.token);
          window.location.href = '/dispatch-console';
        } catch (err) {
          message.error(`Failed to impersonate: ${(err as Error).message}`);
        }
      },
    });
  }, [impersonateTenant]);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
    clearSelectedTenant();
  }, [clearSelectedTenant]);

  const handleDrawerSuspend = useCallback(async (id: number, reason: string) => {
    await suspendTenant(id, reason);
    fetchTenantDetails(id);
    fetchTenants();
  }, [suspendTenant, fetchTenantDetails, fetchTenants]);

  const handleDrawerResume = useCallback(async (id: number) => {
    await resumeTenant(id);
    fetchTenantDetails(id);
    fetchTenants();
  }, [resumeTenant, fetchTenantDetails, fetchTenants]);

  const handleDrawerCancel = useCallback(async (id: number, reason: string) => {
    await cancelTenant(id, reason);
    fetchTenantDetails(id);
    fetchTenants();
  }, [cancelTenant, fetchTenantDetails, fetchTenants]);

  const handleDrawerChangePlan = useCallback(async (id: number, planSlug: string) => {
    await changePlan(id, planSlug);
    fetchTenantDetails(id);
    fetchTenants();
  }, [changePlan, fetchTenantDetails, fetchTenants]);

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} />}

      {/* Stats Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderLeft: '4px solid #1976D2' }} styles={{ body: { padding: 20 } }}>
              <Statistic
                title="Total Tenants"
                value={stats.totalTenants}
                prefix={<TeamOutlined />}
                suffix={<Tag color="blue" style={{ marginLeft: 8, fontSize: 12 }}>{stats.activeTenants} active</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderLeft: '4px solid #2E7D32' }} styles={{ body: { padding: 20 } }}>
              <Statistic
                title="Monthly Revenue"
                value={stats.mrr}
                prefix={<DollarOutlined />}
                precision={0}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderLeft: '4px solid #F57C00' }} styles={{ body: { padding: 20 } }}>
              <Statistic
                title="Trial Tenants"
                value={stats.trialTenants}
                prefix={<ClockCircleOutlined />}
                suffix={<Tag color="orange" style={{ marginLeft: 8, fontSize: 12 }}>{stats.expiringTrials} expiring</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderLeft: '4px solid #C62828' }} styles={{ body: { padding: 20 } }}>
              <Statistic
                title="Suspended"
                value={stats.suspendedTenants}
                prefix={<WarningOutlined />}
                valueStyle={{ color: stats.suspendedTenants > 0 ? '#C62828' : undefined }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '12px 16px' } }}>
        <Space wrap>
          <Search
            placeholder="Search tenants…"
            aria-label="Search tenants"
            value={searchQuery}
            onChange={(e) => updateSearchParams({ q: e.target.value })}
            onSearch={handleSearch}
            style={{ width: 300 }}
            allowClear
            autoComplete="off"
          />
          <Select
            mode="multiple"
            placeholder="Plan"
            aria-label="Filter by plan"
            value={selectedPlans}
            onChange={(v: PlanType[]) => updateSearchParams({ plan: v.join(',') })}
            options={plans.map(p => ({ label: p.name, value: p.slug }))}
            style={{ minWidth: 160 }}
            allowClear
          />
          <Select
            mode="multiple"
            placeholder="Status"
            aria-label="Filter by status"
            value={selectedStatuses}
            onChange={(v: SubscriptionStatus[]) => updateSearchParams({ status: v.join(',') })}
            options={statusOptions}
            style={{ minWidth: 160 }}
            allowClear
          />
          <span style={{ color: '#999', fontSize: 13 }}>
            {total} tenant{total !== 1 ? 's' : ''}
          </span>
        </Space>
      </Card>

      {/* Tenant Table */}
      <AdminTenantList
        tenants={tenants}
        loading={loading}
        total={total}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onViewDetails={handleViewDetails}
        onSuspend={handleSuspend}
        onResume={handleResume}
        onImpersonate={handleImpersonate}
      />

      {/* Tenant Drawer */}
      <AdminTenantDrawer
        tenant={selectedTenant}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        employees={employees}
        operations={operations}
        financials={financials}
        apiUsage={apiUsage}
        auditLogs={auditLogs}
        plans={plans}
        onFetchEmployees={fetchTenantEmployees}
        onFetchOperations={fetchTenantOperations}
        onFetchFinancials={fetchTenantFinancials}
        onFetchApiUsage={fetchTenantApiUsage}
        onFetchAuditLogs={fetchTenantAuditLogs}
        onSuspend={handleDrawerSuspend}
        onResume={handleDrawerResume}
        onCancel={handleDrawerCancel}
        onChangePlan={handleDrawerChangePlan}
      />
    </div>
  );
};

export default AdminDashboard;
